// ==========================================================================
// paginationTelemetry.js
// LocalStorage-gated switches for pagination debugging features.
//
// Canonical key (root): localStorage.setItem(
//   '_debug',
//   '{"pagination":{"woAdjust":true,"telemetryLogs":true,"printDevPreview":true}}'
// )
// Only `_debug.pagination.*` is supported (no legacy key fallback).
// Consumed by usePagination (pagination trigger logs), paginationEngine (WO debug
// root class + reflow telemetry gate), usePrintDocumentV2 (empty decoupage + planner
// diagnostic warns via useLogger). See
// src/pages/report-workspace/V2/pagination/readme/print-v2-pagination-contract.md
// section "Pagination Reflow Telemetry" for telemetry detail.
// ==========================================================================

export const DEBUG_STORAGE_ROOT_KEY = '_debug';
export const PAGINATION_DEBUG_ROOT_NAMESPACE = 'pagination';

// Declarative allowlist for pagination debug flags and related metadata.
export const PAGINATION_DEBUG_FLAGS = Object.freeze({
  // No visual side-effects metadata needed; value is boolean gate only.
  telemetryLogs: Object.freeze({}),
  woAdjust: Object.freeze({
    rootClassName: 'debug-wo-adjust',
  }),
  /** When true, V2 print opens the rendered iframe in a popup instead of window.print(). */
  printDevPreview: Object.freeze({}),
});

const TELEMETRY_ENABLED_VALUES = new Set(['1', 'true', 'yes', 'on', 'debug']);
const TELEMETRY_DISABLED_VALUES = new Set(['0', 'false', 'no', 'off']);

/**
 * Normalize supported localStorage flag payloads into boolean or null.
 *
 * Decision path:
 * 1) booleans are used as-is
 * 2) numbers map to 1=true, everything else=false
 * 3) strings are parsed through explicit allowlists
 * 4) unknown payloads return null (treated as disabled by callers)
 *
 * Purity: pure.
 */
const parseFlagValue = (rawValue) => {
  if (typeof rawValue === 'boolean') return rawValue;
  if (typeof rawValue === 'number') return rawValue === 1;

  const normalized = String(rawValue || '')
    .trim()
    .toLowerCase();
  if (TELEMETRY_ENABLED_VALUES.has(normalized)) return true;
  if (TELEMETRY_DISABLED_VALUES.has(normalized)) return false;
  return null;
};

const readDebugRootObject = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DEBUG_STORAGE_ROOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed;
  } catch (_error) {
    return null;
  }
};

const readPaginationDebugObject = () => {
  const debugRoot = readDebugRootObject();
  const scoped = debugRoot?.[PAGINATION_DEBUG_ROOT_NAMESPACE];
  if (scoped && typeof scoped === 'object' && !Array.isArray(scoped)) {
    return scoped;
  }
  return null;
};

/**
 * Generic check for supported pagination debug flags.
 * Reads from `localStorage._debug.pagination[flagName]`.
 *
 * @param {'telemetryLogs' | 'woAdjust' | 'printDevPreview'} flagName
 * @returns {boolean}
 */
export const isPaginationDebugFlagEnabled = (flagName) => {
  if (typeof window === 'undefined' || !PAGINATION_DEBUG_FLAGS[flagName]) {
    return false;
  }

  // Single-source decision path:
  // 1) read `_debug` root object
  // 2) read `_debug.pagination`
  // 3) parse the requested field value
  // 4) default to false for missing/invalid payloads
  const debugObject = readPaginationDebugObject();
  if (
    debugObject &&
    Object.prototype.hasOwnProperty.call(debugObject, flagName)
  ) {
    const parsedObjectValue = parseFlagValue(debugObject[flagName]);
    if (parsedObjectValue !== null) return parsedObjectValue;
  }
  return false;
};

/**
 * Convenience snapshot of all pagination debug flags.
 *
 * @returns {{telemetryLogs: boolean, woAdjust: boolean, printDevPreview: boolean}}
 */
export const getPaginationDebugFlags = () => ({
  telemetryLogs: isPaginationDebugFlagEnabled('telemetryLogs'),
  woAdjust: isPaginationDebugFlagEnabled('woAdjust'),
  printDevPreview: isPaginationDebugFlagEnabled('printDevPreview'),
});
